/**
 * The fuller half of an item's page: editions, the books under a series, the
 * seasons of a show.
 *
 * All of it comes from `item.details`, which is display-only — nothing here
 * can produce an event, so nothing here needs to be conservative. It exists
 * because a watchlist of bare titles and dates is a spreadsheet: what makes a
 * thing recognisable is its cover, its blurb, and who reads it aloud.
 *
 * Every field is optional at the source, so every row here is written to be
 * absent rather than empty. "Narrated by —" is worse than no line at all.
 */

import { useState } from 'react';
import type { EditionDetail, SeasonDetail, WorkDetail } from '../../details/types.js';
import type { DateSlot } from '../../poller/types.js';
import { loadSeason, type Episode } from '../client.ts';
import { FormatIcon } from '../components/FormatIcon.tsx';
import { SeriesFan } from '../components/SeriesFan.tsx';
import { Thumb } from '../components/Thumb.tsx';
import {
  expectedYear,
  headlineRelease,
  placeholderFor,
  regionalReleases,
  seriesFan,
  stalenessOf,
} from '../lib/derive.js';
import {
  formatDate,
  formatDetailDate,
  formatMinutes,
  formatPosition,
  formatRuntime,
} from '../lib/format.js';
import { FORMAT_SLOTS, formatLabel } from '../lib/formats.js';
import type { WatchItem } from '../lib/model.js';

/** Long lists are collapsed: a series can run to forty books plus reissues. */
const FIRST_SHOWN = 8;

/**
 * What is worth saying about one edition, in the order a person would ask.
 *
 * Rendered under its format's own date row rather than in a section of its
 * own: "Audiobook — 1 Apr 2025" and "Audiobook — read by X" are one fact about
 * one thing, and splitting them made the page say every format name twice.
 *
 * The narrator leads for audio, because it is what decides whether you want
 * that edition at all.
 */
export function editionMeta(edition: EditionDetail): string | null {
  const bits: string[] = [];
  if (edition.narrator) bits.push(`read by ${edition.narrator}`);
  if (edition.runtimeSeconds) bits.push(formatRuntime(edition.runtimeSeconds));
  if (edition.pages) bits.push(`${edition.pages} pages`);
  if (edition.publisher) bits.push(edition.publisher);
  // Only worth mentioning where it might explain why you cannot get it — and
  // it says where the edition *is* published, never where it isn't.
  if (edition.country && edition.format === 'audiobook') bits.push(edition.country);
  return bits.length ? bits.join(' · ') : null;
}

/** "Book 3 of Shadow of the Leviathan" — and a way to get there. */
export function PartOf({
  item,
  followed,
  onOpen,
}: {
  item: WatchItem;
  /** The series watchable, if this reader already follows it. */
  followed: WatchItem | null;
  onOpen: (item: WatchItem) => void;
}) {
  const partOf = item.details?.partOf;
  if (!partOf) return null;

  const position = formatPosition(partOf.position, item.medium);
  const label = position ? `${position} of ${partOf.name}` : `Part of ${partOf.name}`;

  if (!followed) {
    // Said, but not offered as a link that goes nowhere. Adding the series is
    // a different decision from adding this book, and the Add screen is where
    // it belongs.
    return (
      <p className="partof">
        {label} <em>· not on your slate</em>
      </p>
    );
  }

  return (
    <button type="button" className="partof link" onClick={() => onOpen(followed)}>
      {label} <span aria-hidden="true">→</span>
    </button>
  );
}

/** The books in a series, or an author's own. */
export function Works({ item }: { item: WatchItem }) {
  const works = item.details?.works ?? [];
  const [all, setAll] = useState(false);
  if (works.length === 0) return null;

  const shown = all ? works : works.slice(0, FIRST_SHOWN);

  return (
    <>
      <SectionLabel count={works.length}>
        {item.kind === 'creator' ? 'Their books' : 'The books'}
      </SectionLabel>
      <ul className="works cols2">
        {shown.map((work) => (
          <li key={work.sourceId}>
            <Thumb src={work.imageUrl ?? null} kind="title" medium={item.medium} size="sm" />
            <span className="work-body">
              <span className="work-title">{work.title}</span>
              <span className="work-meta">{workLine(work, item.medium)}</span>
            </span>
          </li>
        ))}
      </ul>
      {works.length > FIRST_SHOWN && (
        <button type="button" className="more" onClick={() => setAll((v) => !v)}>
          {all ? 'Show fewer' : `All ${works.length}`} {all ? '▲' : '▼'}
        </button>
      )}
    </>
  );
}

function workLine(work: WorkDetail, medium: WatchItem['medium']): string {
  const position = formatPosition(work.position, medium);
  const date = work.date ? formatDetailDate(work.date) : 'no date yet';
  return position ? `${position} · ${date}` : date;
}

/**
 * Seasons, with their episodes behind a tap.
 *
 * Episodes are fetched when opened rather than stored: a long-running show has
 * hundreds and nobody is waiting for a particular one from years ago.
 */
export function Seasons({ item }: { item: WatchItem }) {
  const seasons = item.details?.seasons ?? [];
  const [open, setOpen] = useState<number | null>(null);
  const [episodes, setEpisodes] = useState<Record<number, Episode[]>>({});
  const [loading, setLoading] = useState<number | null>(null);
  const [failed, setFailed] = useState<number | null>(null);

  if (seasons.length === 0) return null;

  async function toggle(season: SeasonDetail) {
    if (open === season.number) {
      setOpen(null);
      return;
    }
    setOpen(season.number);
    setFailed(null);
    if (episodes[season.number]) return;

    setLoading(season.number);
    try {
      const list = await loadSeason(item.sourceId, season.number);
      setEpisodes((current) => ({ ...current, [season.number]: list }));
    } catch {
      setFailed(season.number);
    } finally {
      setLoading(null);
    }
  }

  return (
    <>
      <SectionLabel count={seasons.length}>Seasons</SectionLabel>
      <ul className="seasons">
        {seasons.map((season) => {
          const isOpen = open === season.number;
          return (
            <li key={season.number}>
              <button type="button" aria-expanded={isOpen} onClick={() => void toggle(season)}>
                <Thumb src={season.imageUrl ?? null} kind="title" medium="tv" size="sm" />
                <span className="season-body">
                  <span className="season-title">{season.name}</span>
                  <span className="season-meta">{seasonLine(season)}</span>
                </span>
                <span className="season-chev" aria-hidden="true">
                  {isOpen ? '▲' : '▼'}
                </span>
              </button>

              {isOpen && (
                <div className="episodes">
                  {loading === season.number && <p className="hint">Loading episodes…</p>}
                  {failed === season.number && (
                    <p className="hint">Couldn&apos;t load those. Try again in a moment.</p>
                  )}
                  {(episodes[season.number] ?? []).map((episode) => (
                    <p key={episode.number} className="episode">
                      <span className="episode-no">{episode.number}</span>
                      <span className="episode-name">{episode.name}</span>
                      <span className="episode-meta">
                        {[
                          episode.date ? formatDetailDate(episode.date) : null,
                          episode.runtimeMinutes ? formatMinutes(episode.runtimeMinutes) : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </p>
                  ))}
                  {/* An announced season with nothing in it yet is the case
                      this app exists for, so it says so rather than sitting
                      empty as though it were still loading. */}
                  {loading !== season.number &&
                    failed !== season.number &&
                    (episodes[season.number] ?? []).length === 0 && (
                      <p className="hint">Nothing listed yet.</p>
                    )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}

function seasonLine(season: SeasonDetail): string {
  const bits: string[] = [];
  if (season.episodes > 0) bits.push(`${season.episodes} episodes`);
  bits.push(season.date ? formatDetailDate(season.date) : 'no date yet');
  return bits.join(' · ');
}

function SectionLabel({ children, count }: { children: React.ReactNode; count?: number }) {
  return (
    <div className="section" style={{ marginTop: 22 }}>
      <div className="section-head">
        <span className="label">{children}</span>
        {count != null && <span className="count">{count}</span>}
      </div>
    </div>
  );
}

const DESCRIPTION_LABEL: Record<string, string> = {
  own: 'What it is',
  series: 'About the series',
  collection: 'About the series',
  author: 'About the author',
};

/**
 * The top of an item's page: what it looks like, what it is called, and what
 * it is about.
 *
 * Shared with the peek screen, which is the point — a thing you looked at
 * before adding it and the same thing afterwards must not be two designs.
 */
export function ItemHead({ item, now }: { item: WatchItem; now: Date }) {
  const [expanded, setExpanded] = useState(false);

  const placeholder = placeholderFor(item, now);
  // Null for everything that is simply a cover — a title, or a series nobody
  // has described yet — and the plain thumbnail handles those.
  const fan = seriesFan(item, now);
  const title = placeholder?.kind === 'placeholder' ? placeholder.label : item.title;

  return (
    <>
      <div className="detail-head">
        {fan ? (
          <SeriesFan
            covers={fan.covers}
            blank={fan.blank}
            title={item.title}
            kind={item.kind}
            medium={item.medium}
          />
        ) : (
          <Thumb src={item.imageUrl} kind={item.kind} medium={item.medium} size="lg" />
        )}
        <span className="detail-headings">
          <span className="eyebrow">
            {mediumWord(item)}
            {item.byline ? ` · ${item.byline}` : ''}
          </span>
          <h2>{title}</h2>
        </span>
      </div>

      {(item.tags.length > 0 || item.rating != null || item.description) && (
        <div className="about">
          {(item.tags.length > 0 || item.rating != null) && (
            <div className="tags">
              {item.tags.slice(0, 3).map((tag) => (
                <span key={tag} className="tag">
                  {tag}
                </span>
              ))}
              {item.completion === 'ongoing' && <span className="tag">Series ongoing</span>}
              {item.rating != null && <span className="tag rating">{item.rating} average</span>}
            </div>
          )}

          {item.description && (
            <>
              {/* Borrowed blurbs are labelled, never passed off as the item's
                  own — that is the only version of a fallback that misleads. */}
              {item.descriptionSource !== 'own' && (
                <p className="blurb-label">{DESCRIPTION_LABEL[item.descriptionSource]}</p>
              )}
              <p className={expanded ? 'blurb' : 'blurb clamped'}>{item.description}</p>
              <button
                type="button"
                className="more"
                aria-expanded={expanded}
                onClick={() => setExpanded((v) => !v)}
              >
                {expanded ? 'Less' : 'More'} {expanded ? '▲' : '▼'}
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
}

/**
 * When it is out, in every form it comes in — including the forms nobody has
 * dated yet, which is the answer people come here for.
 */
export function ItemDates({ item, now }: { item: WatchItem; now: Date }) {
  const placeholder = placeholderFor(item, now);
  const isPlaceholder = placeholder?.kind === 'placeholder';

  // The dates as this reader should see them: their region's, where the
  // poller has them, falling through to the shared answer where it doesn't.
  const releases = regionalReleases(item);
  // The headline is the date *you* are waiting for. With audiobook chosen, a
  // hardback in March is not the answer to "when can I have this".
  const soonest = headlineRelease(item);
  const localised =
    item.region != null && item.snapshot?.kind === 'title' && item.snapshot.regions?.[item.region]
      ? item.region
      : null;

  return (
    <>
      {isPlaceholder ? (
        <PlaceholderBody item={item} now={now} />
      ) : (
        <>
          <div className={soonest ? 'status-strip' : 'status-strip quiet'}>
            <span className="k">
              {soonest
                ? soonest.slot === 'primary'
                  ? releaseWord(item)
                  : formatLabel(soonest.slot, item.medium)
                : 'No date'}
              {/* Whose date this is, whenever it is anyone's in particular. */}
              {localised && <em> · {localised}</em>}
            </span>
            <span className="v">{soonest ? formatDate(soonest.date) : 'not announced'}</span>
          </div>

          {/* Every format the thing could arrive in, chosen or not. Filtering
              this list to the formats you picked would hide the very thing
              you'd want to check — that the audiobook still isn't dated. */}
          <ul className="rows">
            {FORMAT_SLOTS[item.medium].map((slot: DateSlot) => {
              const date = releases[slot] ?? null;
              const wanted = item.formats.length === 0 || item.formats.includes(slot);
              const edition = item.details?.editions?.find((e) => e.format === slot);
              const meta = edition ? editionMeta(edition) : null;
              return (
                <li key={slot}>
                  <span className="rowline">
                    <span className="k">
                      <span className={wanted ? 'rowicon' : 'rowicon muted'}>
                        <FormatIcon slot={slot} medium={item.medium} size={15} />
                      </span>
                      {formatLabel(slot, item.medium)}
                      {!wanted && <em className="aside"> not watching</em>}
                    </span>
                    <span className={date ? 'v' : 'v pending'}>
                      {date ? formatDate(date) : 'not announced'}
                    </span>
                  </span>
                  {/* The narrator, the running time, the publisher — under the
                      format they belong to rather than in a section of their
                      own, which said every format name twice. */}
                  {meta && <span className="rowmeta">{meta}</span>}
                </li>
              );
            })}
          </ul>
        </>
      )}

      {item.details?.runtimeMinutes != null && item.medium !== 'book' && (
        <ul className="rows">
          <li>
            <span className="k">Running time</span>
            <span className="v">{formatMinutes(item.details.runtimeMinutes)}</span>
          </li>
          {item.details.networks?.length ? (
            <li>
              <span className="k">On</span>
              <span className="v">{item.details.networks.join(', ')}</span>
            </li>
          ) : null}
        </ul>
      )}
    </>
  );
}

/**
 * A book nobody has announced. Everything here is inferred, so the screen
 * shows its working rather than asserting a year and hoping to be believed.
 */
function PlaceholderBody({ item, now }: { item: WatchItem; now: Date }) {
  const published = item.snapshot?.kind === 'collection' ? item.snapshot.works.length : 0;
  const expected = expectedYear(item, now);
  const stale = stalenessOf(item, now);

  return (
    <>
      <div className="status-strip quiet">
        <span className="k">No date</span>
        <span className="v">not announced</span>
      </div>

      <ul className="rows">
        <li>
          <span className="k">Books published</span>
          <span className="v">{published}</span>
        </li>
        {item.medianGapDays != null && (
          <li>
            <span className="k">Typical gap</span>
            <span className="v">{describeGap(item.medianGapDays)}</span>
          </li>
        )}
        <li>
          <span className="k">Next expected</span>
          <span className="v pending">{expected ? `${expected}, estimated` : 'unknown'}</span>
        </li>
        {item.expectedTotal != null && (
          <li>
            <span className="k">Series length</span>
            <span className="v pending">{item.expectedTotal}, author's estimate</span>
          </li>
        )}
        {stale && (
          <li>
            <span className="k">Last one</span>
            <span className="v pending">{stale.replace('nothing since ', '')}</span>
          </li>
        )}
      </ul>

      {/* The rows above already say what the note says, so it would only
          repeat itself here. It earns its place on the home list, where
          there is no room to show the working. */}
      {item.expectedTotalNote && <p className="notice">{item.expectedTotalNote}</p>}
    </>
  );
}

function describeGap(days: number): string {
  const months = Math.round(days / 30);
  if (months >= 18) {
    const years = days / 365;
    return `~${years >= 1.75 && years < 2.25 ? 2 : Math.round(years)} years`;
  }
  return `~${months} months`;
}

function mediumWord(item: WatchItem): string {
  if (item.kind === 'creator') return item.medium === 'book' ? 'Author' : 'Director';
  if (item.kind === 'series') return 'Series';
  return item.medium === 'film' ? 'Film' : item.medium === 'tv' ? 'TV' : 'Book';
}

export function releaseWord(item: WatchItem): string {
  if (item.medium === 'film') return 'In cinemas';
  if (item.medium === 'tv') return 'Next episode';
  return 'Published';
}
